import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { MdCheckCircle, MdCancel, MdVisibility, MdPending, MdWarning } from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatCard from '../../../shared/layout/cards/StatCard';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import { fetchRetailerPendingBills, fetchRetailerPendingBillsStats } from '../../../services/Http/Api';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../context/CurrencyProvider';
import { DEFAULT_CURRENCY } from '../../../utils/currencyFormatter';

const RetailerBillApprovals = () => {
    const { t } = useTranslation();
    const { formatAmountIn } = useCurrency();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [perPage, setPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [statusFilter, setStatusFilter] = useState('pending_retailer');
    const statusOptions = useMemo(() => ([
        { value: '', label: t('BillApprovals.filters.all') },
        { value: 'pending_retailer', label: t('BillApprovals.status.pending') },
        { value: 'retailer_approved', label: t('BillApprovals.status.approved') },
        { value: 'retailer_rejected', label: t('BillApprovals.status.rejected') },
    ]), [t]);

    const loadStats = async () => {
        try {
            const data = await fetchRetailerPendingBillsStats();
            setStats(data);
        } catch (err) {
            // non-blocking
        }
    };

    const loadBills = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchRetailerPendingBills({
                per_page: perPage,
                page: currentPage,
                status: statusFilter || undefined,
            });
            const rowsData = data?.data || data || [];
            const meta = data?.meta;
            setRows(rowsData);
            setTotalRows(meta?.total ?? rowsData.length);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [perPage, currentPage, statusFilter]);

    useEffect(() => {
        loadStats();
        loadBills();
    }, [loadBills]);

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending_retailer: { label: t('BillApprovals.status.pending'), color: '#F59E0B', bg: '#FEF3C7' },
            retailer_approved: { label: t('BillApprovals.status.approved'), color: '#22C55E', bg: '#DCFCE7' },
            retailer_rejected: { label: t('BillApprovals.status.rejected'), color: '#EF4444', bg: '#FEE2E2' },
        };
        const config = statusConfig[status] || { label: status, color: '#6B7280', bg: '#F3F4F6' };
        return <StatusBadge $color={config.color} $bg={config.bg}>{config.label}</StatusBadge>;
    };

    const getBillCurrencyCode = (row) => {
        const code = row?.currency_code || row?.currency || DEFAULT_CURRENCY;
        if (typeof code !== 'string') return DEFAULT_CURRENCY;
        const normalized = code.trim().toUpperCase();
        return normalized.length >= 3 && normalized.length <= 4 ? normalized : DEFAULT_CURRENCY;
    };

    const columns = useMemo(() => ([
        {
            name: t('BillApprovals.columns.date'),
            selector: row => row.bill_date || row.created_at,
            sortable: true,
            width: '140px',
            cell: row => new Date(row.bill_date || row.created_at).toLocaleDateString(),
        },
        {
            name: t('BillApprovals.columns.customer'),
            selector: row => row.user?.name || row.user?.email || '-',
            sortable: false,
            grow: 1.2,
        },
        {
            name: t('BillApprovals.columns.venue'),
            selector: row => row.venue?.name || '-',
            sortable: false,
            grow: 1,
        },
        {
            name: t('BillApprovals.columns.billNumber'),
            selector: row => row.bill_number || row.external_bill_id || '-',
            sortable: false,
            width: '140px',
        },
        {
            name: t('BillApprovals.columns.total'),
            selector: row => row.total_amount,
            sortable: true,
            width: '120px',
            cell: row => row.total_amount
                ? formatAmountIn(row.total_amount, getBillCurrencyCode(row), { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : '-',
        },
        {
            name: t('BillApprovals.columns.items'),
            selector: row => row.items_count || row.items?.length || 0,
            sortable: false,
            width: '80px',
        },
        {
            name: t('BillApprovals.columns.status'),
            selector: row => row.status,
            sortable: true,
            width: '140px',
            cell: row => getStatusBadge(row.status),
        },
        {
            name: t('BillApprovals.columns.actions'),
            cell: row => (
                <ActionsCell>
                    <ActionButton
                        onClick={() => navigate(`/bill-approvals/${row.id}`)}
                        title={t('BillApprovals.actions.view')}
                    >
                        <MdVisibility size={18} />
                    </ActionButton>
                </ActionsCell>
            ),
            width: '100px',
        },
    ]), [formatAmountIn, navigate, t]);

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('BillApprovals.title')}</PageTitle>
                <Subtitle>{t('BillApprovals.subtitle')}</Subtitle>
            </PageHeader>
            <PageContent>
                {stats && (
                    <StatsGrid>
                        <StatCard
                            title={t('BillApprovals.stats.pending')}
                            value={stats.pending ?? 0}
                            icon={<MdPending />}
                            color="warning"
                        />
                        <StatCard
                            title={t('BillApprovals.stats.approved')}
                            value={stats.approved ?? 0}
                            icon={<MdCheckCircle />}
                            color="success"
                        />
                        <StatCard
                            title={t('BillApprovals.stats.rejected')}
                            value={stats.rejected ?? 0}
                            icon={<MdCancel />}
                            color="danger"
                        />
                        <StatCard
                            title={t('BillApprovals.stats.thisWeek')}
                            value={stats.this_week ?? 0}
                            icon={<MdWarning />}
                            color="info"
                        />
                    </StatsGrid>
                )}

                <FilterBar>
                    <FilterGroup>
                        <FilterLabel htmlFor="status-filter">
                            {t('BillApprovals.filters.status')}
                        </FilterLabel>
                        <FilterSelect
                            id="status-filter"
                            value={statusFilter}
                            onChange={(event, nextValue) => setStatusFilter(nextValue ?? event?.target?.value ?? '')}
                            options={statusOptions}
                            inputId="status-filter"
                        />
                    </FilterGroup>
                </FilterBar>

                {loading ? (
                    <LoadingMessage>{t('BillApprovals.loading')}</LoadingMessage>
                ) : error ? (
                    <ErrorMessage>{t('BillApprovals.error')}</ErrorMessage>
                ) : rows.length === 0 ? (
                    <EmptyState>
                        <MdCheckCircle size={48} />
                        <h3>{t('BillApprovals.empty.title')}</h3>
                        <p>{t('BillApprovals.empty.description')}</p>
                    </EmptyState>
                ) : (
                    <TableWrapper>
                        <DataTable
                            columns={columns}
                            data={rows}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangeRowsPerPage={setPerPage}
                            onChangePage={setCurrentPage}
                            highlightOnHover
                            pointerOnHover
                            onRowClicked={(row) => navigate(`/bill-approvals/${row.id}`)}
                            customStyles={tableStyles}
                        />
                    </TableWrapper>
                )}
            </PageContent>
        </PageContainer>
    );
};

export default RetailerBillApprovals;

// Custom table styles
const tableStyles = {
    headRow: {
        style: {
            backgroundColor: '#F9FAFB',
            borderBottom: '1px solid #E5E7EB',
        },
    },
    headCells: {
        style: {
            fontWeight: '600',
            color: '#374151',
            fontSize: '13px',
        },
    },
    rows: {
        style: {
            '&:hover': {
                backgroundColor: '#F3F4F6',
            },
        },
    },
};

// Styled Components
const Subtitle = styled.p`
    margin: 0.25rem 0 0 0;
    color: #6B7280;
    font-size: 0.875rem;
`;

const FilterBar = styled.div`
    display: flex;
    gap: 1rem;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
    align-items: center;
`;

const FilterGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const FilterLabel = styled.label`
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
`;

const FilterSelect = styled(SimpleSelect)`
    width: auto;
    min-width: 220px;
    font-size: 0.875rem;
    cursor: pointer;
`;

const TableWrapper = styled.div`
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.625rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${({ $color }) => $color};
    background: ${({ $bg }) => $bg};
`;

const ActionsCell = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 6px;
    background: #F3F4F6;
    color: #6B7280;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: rgba(130, 22, 146, 0.1);
        color: rgba(130, 22, 146, 1);
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3.75rem 1.25rem;
    text-align: center;
    color: #6B7280;

    svg {
        color: #22C55E;
        margin-bottom: 1rem;
    }

    h3 {
        margin: 0 0 0.5rem 0;
        color: #374151;
        font-size: 1.125rem;
    }

    p {
        margin: 0;
        font-size: 0.875rem;
    }
`;
